/**
 * THY AppFeedback için oturum uygunluğu ve belirteç sağlama (096).
 *
 * **Bu modül bilinçli olarak `auth/` altındadır**: işi kimlik/oturum okumaktır — `lib/` altındaki
 * uyarlama katmanına değil, auth katmanına aittir.
 */

import { readActingUserId } from './actingUser'
import { getAccessToken } from './userManager'

/**
 * Bileşene verilecek belirteç. **Yalnızca bellekte** tutulur.
 *
 * FR-010 belirtecin ikinci bir *kalıcı* kopyasının çıkarılmasını yasaklar: kılavuzun önerdiği
 * `tokenKey`/`tokenStorage` yolu belirteci web deposuna kopyalamayı ve onu yenilenme/çıkış/persona
 * devrinde senkron tutmayı gerektirirdi. Bayatlayan böyle bir kopya, feature 094'te (çıkış sonrası
 * token reddi) uğraşılan hata sınıfını geri getirir.
 */
let cachedToken: string | null = null

/**
 * Bu oturumda anket gösterilebilir mi?
 *
 * Vekâlet modunda **gösterilmez** (FR-014): geri bildirim isteği yapan kimliğe yazılır, dolayısıyla
 * vekâleten verilen puan ekranı kullanan kişinin değil adına çalışılan kişinin deneyimi olarak
 * kaydedilir ve THY'nin ölçümünü bozar.
 *
 * Kontrol edilen mekanizma **089 vekâlet personasıdır** (`actingUser`): kullanıcının kendi
 * arayüzünden geçtiği persona. Bu modda Görev *oluşturma* sunucuda düşürülür, ama **statü değiştirme
 * bilinçli olarak serbesttir** — yani bu kontrol olmasaydı, başkası adına "Tamamlandı" yapan bir
 * kullanıcıya anket açılırdı. FR-014'ün yasakladığı durumun tam olarak kendisi.
 */
export function isAppFeedbackSessionEligible(): boolean {
  return readActingUserId() == null
}

/**
 * Belirteci alıp eşzamanlı okunabilir hâle getirir. `init()` çağrılmadan **önce** çalıştırılmalıdır.
 *
 * Neden gerekli: bileşen belirteci **eşzamanlı** ister (yetkilendirme başlığını dize
 * birleştirmeyle kurar), oysa
 * uygulamanın belirteç okuyucusu `async`'tir ve normal OIDC oturumu için depoda eşzamanlı bir
 * okuyucu yoktur. Tetikleme zaten bir `onSuccess` geri çağrısıdır; orada `await` etmek serbesttir
 * ve kullanıcının işini geciktirmez (istek çoktan tamamlanmıştır).
 *
 * @returns kullanılabilir bir belirteç elde edildiyse `true`
 */
export async function primeAppFeedbackToken(): Promise<boolean> {
  try {
    const token = await getAccessToken()
    cachedToken = typeof token === 'string' && token.length > 0 ? token : null
  } catch {
    // Oturum okunamıyorsa anket gösterilmez — ama bu kullanıcının asıl işini ETKİLEMEZ (FR-007).
    cachedToken = null
  }
  return cachedToken !== null
}

/**
 * Hazırlanmış belirteci eşzamanlı döndürür. Bileşenin `getToken` alanına bu bağlanır.
 *
 * **Bilinen sınırlama:** anket açıkken belirteç yenilenirse buradaki değer bayatlayabilir. Pencere
 * dört dakikayla sınırlıdır (bileşen kendini kapatır). En kötü durumda gönderim yetkisiz döner,
 * bileşen kendi hata ekranını gösterir ve kullanıcının asıl işi etkilenmez.
 */
export function readAppFeedbackToken(): string | null {
  return cachedToken
}

/** Belirteci bellekten siler (oturum sonu / kimlik değişimi). */
export function clearAppFeedbackToken(): void {
  cachedToken = null
}
